import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { NeonTooltip } from '@ps-refarch-ux/neon';

type TruncatingFieldBaseProps = {
  tooltipId: string;
  tooltipText: string;
  children: ReactNode;
};

type TruncatingFieldSpanProps = TruncatingFieldBaseProps & {
  variant: 'span';
};

type TruncatingFieldButtonProps = TruncatingFieldBaseProps & {
  variant: 'button';
  onClick: () => void;
};

export type CourseCatalogTruncatingFieldProps =
  | TruncatingFieldSpanProps
  | TruncatingFieldButtonProps;

/** Course catalog text cell: NeonTooltip only when the label is truncated (ellipsis). */
export function CourseCatalogTruncatingField(
  props: CourseCatalogTruncatingFieldProps
): ReactElement {
  const { tooltipId, tooltipText, children, variant } = props;
  const ref = useRef<HTMLSpanElement | HTMLButtonElement>(null);
  const [truncated, setTruncated] = useState(false);

  const updateTruncation = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    // Small tolerance for subpixel layout / font rounding
    setTruncated(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useLayoutEffect(() => {
    updateTruncation();
  }, [tooltipText, updateTruncation]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const ro = new ResizeObserver(() => updateTruncation());
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateTruncation]);

  const inner =
    variant === 'button' ? (
      <button
        type="button"
        id={tooltipId}
        ref={ref as React.RefObject<HTMLButtonElement>}
        className="course-link cc-cell-text"
        onClick={props.onClick}
      >
        {children}
      </button>
    ) : (
      <span id={tooltipId} ref={ref as React.RefObject<HTMLSpanElement>} className="cc-cell-text">
        {children}
      </span>
    );

  return (
    <div className="cc-cell-truncate">
      {inner}
      {truncated ? <NeonTooltip dataTargetId={tooltipId}>{tooltipText}</NeonTooltip> : null}
    </div>
  );
}
